import React, { useState } from "react";
import {
  Box,
  TextField,
  Button,
  MenuItem,
  Typography,
  Input,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Alert,
} from "@mui/material";
import { getFunctions, httpsCallable } from "firebase/functions";
import { collection, query, where, getDocs } from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { db, storage } from "../../firebase";
import { logEvent } from "../../utils/LogEvent";

function CreateAccount() {
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [employeeCode, setEmployeeCode] = useState("");
  const [department, setDepartment] = useState("");
  const [position, setPosition] = useState("");
  const [role, setRole] = useState("user");
  const [status, setStatus] = useState("active");
  const [contactNumber, setContactNumber] = useState("");
  const [profilePicFile, setProfilePicFile] = useState(null);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [generatedPassword, setGeneratedPassword] = useState("");

  // Dropdown options
  const departmentOptions = [
    "Engineering",
    "Computer Studies and Multimedia Arts",
    "HSC",
    "MPS",
  ];
  const positionOptions = [
    "Program Director",
    "Director",
    "Assistant Director",
    "Professor",
  ];
  const roleOptions = ["admin", "user"];
  const statusOptions = ["active", "inactive", "pending"];

  // Password generation: surname + last 4 digits of contact number
  const generatePassword = (fullName, contactNumber) => {
    const nameParts = fullName.trim().split(" ");
    const surname = nameParts[nameParts.length - 1];
    const digits = contactNumber.replace(/\\D/g, "");
    const last4 = digits.slice(-4);
    return surname + last4;
  };

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      setProfilePicFile(e.target.files[0]);
    }
  };

  const validateInputs = () => {
    // Fix the regex pattern - remove escaped backslashes
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      return "Invalid email format.";
    }

    // Fix the contact number regex - remove escaped backslashes
    if (!/^0\d{10}$/.test(contactNumber)) {
      return "Invalid mobile number. Must be 11 digits beginning with 0.";
    }

    // Check if employee code exceeds 10 digits or contains non-numeric characters
    if (!/^\d{1,10}$/.test(employeeCode)) {
      return "Employee code must be numeric and cannot exceed 10 digits.";
    }

    return "";
  };

  // Check for duplicates in the database
  const validateUniqueData = async () => {
    const usersRef = collection(db, "users");

    const emailQuery = query(usersRef, where("email", "==", email));
    const emailSnapshot = await getDocs(emailQuery);
    if (!emailSnapshot.empty) return "Email already exists.";

    const empQuery = query(usersRef, where("employeeCode", "==", employeeCode));
    const empSnapshot = await getDocs(empQuery);
    if (!empSnapshot.empty) return "Employee code already exists.";

    const phoneQuery = query(
      usersRef,
      where("contactNumber", "==", contactNumber)
    );
    const phoneSnapshot = await getDocs(phoneQuery);
    if (!phoneSnapshot.empty) return "Contact number already exists.";

    const nameQuery = query(usersRef, where("fullName", "==", fullName));
    const nameSnapshot = await getDocs(nameQuery);
    if (!nameSnapshot.empty)
      return "A user with this full name already exists.";

    return "";
  };

  // Upload profile picture and get URL
  const uploadProfilePicture = async (uid) => {
    if (!profilePicFile) return null;

    const storageRef = ref(storage, `profile_pictures/${uid}`);
    await uploadBytes(storageRef, profilePicFile);
    return await getDownloadURL(storageRef);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage("");
    setError("");
    setLoading(true);

    try {
      // Validate inputs
      const inputError = await validateInputs();
      if (inputError) {
        setError(inputError);
        setLoading(false);
        return;
      }

      // Check for duplicates
      const uniqueError = await validateUniqueData();
      if (uniqueError) {
        setError(uniqueError);
        setLoading(false);
        return;
      }

      // Generate password
      const password = generatePassword(fullName, contactNumber);
      console.log("Generated password:", password);
      setGeneratedPassword(password);

      // Prepare user data for the Cloud Function
      const userData = {
        email,
        password,
        fullName,
        employeeCode,
        department,
        position,
        role,
        status,
        contactNumber,
      };

      console.log("About to call createUser with:", userData);

      // Get reference to the function with proper region
      const functions = getFunctions(undefined, "asia-southeast1");
      const createUserFunction = httpsCallable(functions, "registerUser");

      // Call the Cloud Function
      const result = await createUserFunction(userData);
      console.log("Function result:", result.data);

      if (result.data.success) {
        // If we have a profile picture, upload it
        if (profilePicFile) {
          try {
            const photoURL = await uploadProfilePicture(result.data.uid);
            console.log("Profile picture uploaded:", photoURL);
          } catch (uploadError) {
            console.error("Error uploading profile picture:", uploadError);
            // Continue anyway, as the user is already created
          }
        }

        // Log the event
        await logEvent(
          "create",
          { email, fullName, employeeCode },
          "adminUID_or_email"
        );

        // Show success dialog
        setOpenDialog(true);
        setMessage("User created successfully");

        // Reset form
        resetForm();
      } else {
        setError("User creation failed. Please try again.");
      }
    } catch (err) {
      console.error("Error creating user:", err);
      setError(`Error: ${err.message || "Unknown error occurred"}`);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFullName("");
    setEmail("");
    setEmployeeCode("");
    setDepartment("");
    setPosition("");
    setRole("user");
    setStatus("active");
    setContactNumber("");
    setProfilePicFile(null);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  return (
    <>
      <Box
        component="form"
        onSubmit={handleSubmit}
        sx={{ p: 2, maxWidth: 600, mx: "auto" }}
      >
        <Typography variant="h5" gutterBottom>
          Create Account
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {message && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {message}
          </Alert>
        )}

        <TextField
          label="Full Name"
          value={fullName}
          onChange={(e) => setFullName(e.target.value)}
          fullWidth
          margin="normal"
          required
        />
        <TextField
          label="Email"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          fullWidth
          margin="normal"
          required
        />
        <TextField
          label="Employee Code"
          value={employeeCode}
          onChange={(e) => {
            const value = e.target.value;
            // Only allow numeric input and max 10 digits
            if (/^\d{0,10}$/.test(value)) {
              setEmployeeCode(value);
              setError("");
            } else if (value.length > 10) {
              setError("Employee code cannot exceed 10 digits.");
            } else if (!/^\d*$/.test(value)) {
              setError("Employee code must contain only numbers.");
            }
          }}
          fullWidth
          margin="normal"
          required
          helperText="Maximum 10 digits (numbers only)"
          error={!/^\d{1,10}$/.test(employeeCode) && employeeCode.length > 0}
          inputProps={{ inputMode: "numeric", pattern: "[0-9]*" }}
        />
        <TextField
          select
          label="Department"
          value={department}
          onChange={(e) => setDepartment(e.target.value)}
          fullWidth
          margin="normal"
          required
        >
          {departmentOptions.map((dept) => (
            <MenuItem key={dept} value={dept}>
              {dept}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          select
          label="Position"
          value={position}
          onChange={(e) => setPosition(e.target.value)}
          fullWidth
          margin="normal"
          required
        >
          {positionOptions.map((pos) => (
            <MenuItem key={pos} value={pos}>
              {pos}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          select
          label="Role"
          value={role}
          onChange={(e) => setRole(e.target.value)}
          fullWidth
          margin="normal"
          required
        >
          {roleOptions.map((r) => (
            <MenuItem key={r} value={r}>
              {r}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          select
          label="Status"
          value={status}
          onChange={(e) => setStatus(e.target.value)}
          fullWidth
          margin="normal"
          required
        >
          {statusOptions.map((stat) => (
            <MenuItem key={stat} value={stat}>
              {stat}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          label="Contact Number"
          value={contactNumber}
          onChange={(e) => setContactNumber(e.target.value)}
          fullWidth
          margin="normal"
          required
        />
        <Box sx={{ my: 2 }}>
          <Typography variant="subtitle1">Profile Picture</Typography>
          <Input
            type="file"
            onChange={handleFileChange}
            inputProps={{ accept: "image/*" }}
          />
        </Box>
        <Button
          variant="contained"
          color="primary"
          type="submit"
          disabled={loading}
          startIcon={loading && <CircularProgress size={20} color="inherit" />}
          sx={{ mt: 2 }}
        >
          {loading ? "Creating Account..." : "Create Account"}
        </Button>
      </Box>

      <Dialog open={openDialog} onClose={handleCloseDialog}>
        <DialogTitle>Account Created Successfully</DialogTitle>
        <DialogContent>
          <Typography variant="body1" gutterBottom>
            The account for {fullName} has been created successfully.
          </Typography>
          <Typography variant="body1" sx={{ mt: 2 }}>
            Autogenerated password: <strong>{generatedPassword}</strong>
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            Please provide this password to the user. They will be able to
            change it after login.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} color="primary">
            OK
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default CreateAccount;
